/**
 * The card backs a player may choose from.
 *
 * IDs are persisted in `cribbage.settings`, so they are storage keys rather
 * than display copy. The images use the card's exact 5:7 proportions. Labels
 * live in i18n beside the settings control.
 */
export const CARD_BACKS = [
  {
    id: 'plain-red',
    src: '/card-backs/plain-red.svg',
  },
  {
    id: 'plain-blue',
    src: '/card-backs/plain-blue.svg',
  },
  {
    id: 'plain-dots-red',
    src: '/card-backs/plain-dots-red.svg',
  },
  {
    id: 'plain-dots-blue',
    src: '/card-backs/plain-dots-blue.svg',
  },
  {
    id: 'mount-agung-collage',
    src: '/card-backs/indonesia-card-03-mount-agung-openai.webp',
  },
  {
    id: 'four-suit-tapestry',
    src: '/card-backs/four-suit-tapestry-openai.webp',
  },
  {
    id: 'perfect-hand',
    src: '/card-backs/perfect-hand-red-replicate.webp',
  },
  {
    id: 'race-to-121',
    src: '/card-backs/race-to-121-openai.webp',
  },
  {
    id: 'crossed-tracks',
    src: '/card-backs/race-to-121-replicate.webp',
  },
  {
    id: 'skunk-heraldry',
    src: '/card-backs/skunk-heraldry-replicate.webp',
  },
  {
    id: 'pegwheel-compass',
    src: '/card-backs/pegwheel-compass-openai.webp',
  },
  {
    id: 'compass-rose',
    src: '/card-backs/pegwheel-compass-replicate.webp',
  },
  {
    id: 'midnight-card-room',
    src: '/card-backs/midnight-card-room-replicate.webp',
  },
  {
    id: 'muggins-magpies',
    src: '/card-backs/muggins-magpies-openai.webp',
  },
  {
    id: 'heraldic-magpies',
    src: '/card-backs/muggins-magpies-replicate.webp',
  },
  {
    id: 'patra-lotus-carving',
    src: '/card-backs/bali-patra-carving-openai.webp',
  },
  {
    id: 'cream-lotus-carving',
    src: '/card-backs/bali-patra-carving-replicate.webp',
  },
  {
    id: 'jepun-batik',
    src: '/card-backs/bali-jepun-batik-openai.webp',
  },
  {
    id: 'jepun-batik-ii',
    src: '/card-backs/bali-jepun-batik-replicate.webp',
  },
  {
    id: 'four-suit-tapestry-ii',
    src: '/card-backs/four-suit-tapestry-v2-openai.webp',
  },
  {
    id: 'four-suit-crest',
    src: '/card-backs/four-suit-tapestry-v3-replicate.webp',
  },
  {
    id: 'lake-temple-collage',
    src: '/card-backs/indonesia-card-01-lake-temple-openai.webp',
  },
  {
    id: 'water-sanctuary-collage',
    src: '/card-backs/indonesia-card-02-water-sanctuary-openai.webp',
  },
  {
    id: 'tengger-caldera-collage',
    src: '/card-backs/indonesia-card-04-tengger-caldera-openai.webp',
  },
  {
    id: 'coconut-crown-collage',
    src: '/card-backs/indonesia-card-05-coconut-crown-openai.webp',
  },
  {
    id: 'padar-island-collage',
    src: '/card-backs/indonesia-card-06-padar-island-openai.webp',
  },
  {
    id: 'raja-ampat-collage',
    src: '/card-backs/indonesia-card-07-raja-ampat-openai.webp',
  },
  {
    id: 'emerald-terraces-collage',
    src: '/card-backs/indonesia-card-08-emerald-terraces-openai.webp',
  },
  {
    id: 'rice-terraces-collage',
    src: '/card-backs/indonesia-card-09-rice-terraces-openai.webp',
  },
  {
    id: 'campuhan-ridge-collage',
    src: '/card-backs/indonesia-card-10-campuhan-ridge-openai.webp',
  },
] as const;

export type CardBack = (typeof CARD_BACKS)[number]['id'];

/** The first back a fresh install sees. */
export const DEFAULT_CARD_BACK: CardBack = 'plain-red';

/**
 * The backs every player may use: the four plain SVG backs. Every other back
 * is for members.
 */
export const FREE_CARD_BACKS: ReadonlySet<CardBack> = new Set<CardBack>([
  'plain-red',
  'plain-blue',
  'plain-dots-red',
  'plain-dots-blue',
]);

const CARD_BACK_ASSETS = new Map<CardBack, string>(
  CARD_BACKS.map((cardBack) => [cardBack.id, cardBack.src]),
);

/** Resolve persisted input defensively; an old or hand-edited value gets the default. */
export function cardBackAsset(cardBack: CardBack): string {
  return CARD_BACK_ASSETS.get(cardBack) ?? cardBackAsset(DEFAULT_CARD_BACK);
}
